import React, { useState, useCallback } from "react";
import { Button, Form } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import { financeAuthPayload } from "../../components/finance/AccountSelect";
import { formatNumber } from "../../utils/formatNumber";
import "../../Styles/yellowcalfcss/Finance.css";

/**
 * GL Tie-Out (Phase D)
 *
 * Reconciles each subledger's balance (fees, bills, assets, liabilities) against
 * its GL control account. A variance means the subledger and the ledger have
 * drifted apart and need investigation.
 *
 * Backend: reports/gl-tie-out. Row shape (defensive):
 *   { subledger, control_account, subledger_balance, gl_balance, variance,
 *     is_tied_out }
 */
const GLTieOut = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const money = (v) => `${customer_currency} ${formatNumber(v || 0)}`;

  const run = useCallback(async () => {
    setLoading(true);
    setRows(null);
    try {
      const res = await simplePostCall(
        ApiConfig.glTieOut,
        JSON.stringify({ ...financeAuthPayload(), as_of_date: asOfDate })
      );
      if (res?.result) {
        setRows(Array.isArray(res.data) ? res.data : []);
      } else {
        setRows(null);
        toast.error(res?.message || "Failed to run tie-out.");
      }
    } catch (err) {
      console.error("GL tie-out error:", err);
      toast.error("An error occurred running the tie-out.");
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  const varianceOf = (r) =>
    r.variance != null
      ? Number(r.variance)
      : (Number(r.subledger_balance) || 0) - (Number(r.gl_balance) || 0);

  const isTied = (r) =>
    r.is_tied_out != null ? !!r.is_tied_out : Math.abs(varianceOf(r)) < 0.01;

  const allTied = rows && rows.length > 0 && rows.every(isTied);

  return (
    <div className="finance-page-container">
      <ToastContainer />

      <div className="special-header panel-white-all">
        <h4 className="page-title">GL Tie-Out</h4>
      </div>

      <div className="yc-list-body">
        <div className="yc-list-filter">
          <Form.Control
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            title="As of date"
          />
          <Button className="btn btn-primary" onClick={run} disabled={loading}>
            {loading ? "Checking..." : "Run Tie-Out"}
          </Button>
        </div>

        {rows && rows.length > 0 && (
          <div className="fin-stat-row">
            <div className="fin-stat-card fin-accent-all">
              <span className="fin-stat-label">Subledgers Checked</span>
              <span className="fin-stat-value">{rows.length}</span>
            </div>
            <div
              className={`fin-stat-card ${
                allTied ? "fin-accent-income" : "fin-accent-expense"
              }`}
            >
              <span className="fin-stat-label">Status</span>
              <span className="fin-stat-value">
                {allTied ? "All Tied Out" : "Variances Found"}
              </span>
            </div>
            <div className="fin-stat-card fin-accent-liability">
              <span className="fin-stat-label">Out of Balance</span>
              <span className="fin-stat-value">
                {rows.filter((r) => !isTied(r)).length}
              </span>
            </div>
          </div>
        )}

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "20vh" }}
          >
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : !rows ? (
          <div className="fin-empty">
            <span className="fin-empty-title">No tie-out run yet</span>
            <span className="fin-empty-text">
              Pick an as-of date and run the tie-out to compare each subledger
              against its GL control account.
            </span>
          </div>
        ) : rows.length === 0 ? (
          <div className="fin-empty">
            <span className="fin-empty-title">Nothing to reconcile</span>
            <span className="fin-empty-text">
              No subledger control accounts were returned for this date.
            </span>
          </div>
        ) : (
          <>
            <div className="yc-list-thead-card">
              <div className="yc-list-grid yc-list-grid-6">
                <div className="yc-list-cell">Sr.No</div>
                <div className="yc-list-cell">Subledger</div>
                <div className="yc-list-cell">Control Account</div>
                <div className="yc-list-cell">Subledger Bal.</div>
                <div className="yc-list-cell">GL Balance</div>
                <div className="yc-list-cell">Variance</div>
              </div>
            </div>
            <div className="yc-list-row-list">
              {rows.map((r, idx) => {
                const variance = varianceOf(r);
                const tied = isTied(r);
                return (
                  <div key={idx} className="yc-list-row-card">
                    <div className="yc-list-grid yc-list-grid-6">
                      <div className="yc-list-cell">{idx + 1}</div>
                      <div className="yc-list-cell fw-semibold">
                        {r.subledger || r.name || "—"}
                      </div>
                      <div className="yc-list-cell">
                        {r.control_account || "—"}
                      </div>
                      <div className="yc-list-cell">
                        {money(r.subledger_balance)}
                      </div>
                      <div className="yc-list-cell">{money(r.gl_balance)}</div>
                      <div className="yc-list-cell">
                        <span
                          className={`fin-status ${
                            tied ? "fin-status-active" : "fin-status-reversed"
                          }`}
                        >
                          {tied ? "Tied" : money(variance)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GLTieOut;
